import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { AisService, getAuthToken, PortCodeService, RegulationService, SearchService, VesselService, SubjectListService } from '../service';
import { Alert, AlertTitle, Box, Button, Card, CardContent, CardHeader, CircularProgress, Divider, Grid, Stack, Tooltip, Typography } from '@mui/material';
import { AbsReactTable, TABLE, LoadingMessage, LoadingMessasge } from 'customer_portal-ui-shared';

import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import FlagIcon from '@mui/icons-material/Flag';
import SouthAmericaIcon from '@mui/icons-material/SouthAmerica';
import InfoIcon from '@mui/icons-material/Info';

import { RegulationList, VesselDestination } from '../component';
import { customUtils } from '../utils';

const tableColumns = [
    {
        Header: 'Vessel Name',
        accessor: 'VesselName_Format'
    },
    {
        Header: <center>IMO Number</center>,
        accessor: 'ImoNumber_Format'
    },
    {
        Header: <center>Class Number</center>,
        accessor: 'ClassNumber_Format'
    },
    {
        Header: 'AIS Timestamp',
        accessor: 'Timestamp_Format'
    },
    {
        Header: (
            <Tooltip
                title={
                    <Alert severity="info">
                        <AlertTitle>AIS Disclaimer</AlertTitle>
                        <span>
                            The first line is the destination information as entered by the vessel’s crew into the AIS to be reported. <br />
                            The second line is the Associated Destination as determined by the Regulatory World Map.
                        </span>
                    </Alert>
                }
                placement="left"
                arrow
            >
                <span>
                    AIS Destination &nbsp;
                    <InfoIcon />
                </span>
            </Tooltip>
        ),
        accessor: 'AisDestination_Format'
    },
    {
        Header: 'Destination Restrictions',
        accessor: 'DestinationRestriction_Format'
    }
];

const refRegulationList = React.createRef();
const refVesselList = React.createRef();

const tablePage = [10, 20, 50, 100];

const DestinationPortToolTip = (props) => {
    const { matchData, handleRegulationData } = props;
    const { instance, accounts } = useMsal();

    const [isLoading, setIsLoading] = useState(true);
    const [summaryData, setSummaryData] = useState();

    useEffect(() => {
        (async () => {
            if (matchData) {
                setIsLoading(true);
                await getData();
                setIsLoading(false);
            }
        })();
    }, [matchData]);

    const getData = async () => {
        const authToken = await getAuthToken(instance, accounts);
        const summaryData = await RegulationService.getSummary(authToken, matchData.MatchResultTypeId, matchData.MatchResultType);

        setSummaryData(summaryData);
    };

    const handleSeeMore = async () => {
        const authToken = await getAuthToken(instance, accounts);
        const regulationPortdata = await RegulationService.getDetail(authToken, matchData.MatchResultTypeId, matchData.MatchResultType);

        handleRegulationData(regulationPortdata, matchData);
    };

    const getMatchLabel = () => {
        const getAvatar = () => {
            if (matchData.MatchResultType === 'Port') {
                return <DirectionsBoatIcon />;
            } else if (matchData.MatchResultType === 'State') {
                return <AccountBalanceIcon />;
            } else if (matchData.MatchResultType === 'Country') {
                return <FlagIcon />;
            } else if (matchData.MatchResultType === 'Region') {
                return <SouthAmericaIcon />;
            }
        };

        const getTitle = () => {
            if (matchData.MatchResultType === 'Port') {
                return <Typography> {matchData.Port}</Typography>;
            } else if (matchData.MatchResultType === 'State') {
                return <Typography> {matchData.State}</Typography>;
            } else if (matchData.MatchResultType === 'Country') {
                return <Typography> {matchData.Country}</Typography>;
            } else if (matchData.MatchResultType === 'Region') {
                return <Typography> {matchData.Region}</Typography>;
            }
        };

        const getSubHeader = () => {
            return (
                <>
                    <Typography variant="caption" display="block">
                        {matchData.MatchResultType === 'Port' && !matchData.State && `${matchData.Country}, ${matchData.Region}`}
                        {matchData.MatchResultType === 'Port' && matchData.State && `${matchData.State}, ${matchData.Country}, ${matchData.Region}`}
                        {matchData.MatchResultType === 'State' && `${matchData.Country}, ${matchData.Region}`}
                        {matchData.MatchResultType === 'Country' && `${matchData.Region}`}
                    </Typography>
                </>
            );
        };

        return (
            <>
                <CardHeader avatar={getAvatar()} title={getTitle()} subheader={getSubHeader()} />
            </>
        );
    };

    return (
        <>
            {isLoading && <Typography>Loading...</Typography>}
            {!isLoading && matchData && summaryData && (
                <>
                    <Card>
                        {getMatchLabel()}
                        <CardContent>
                            <Stack direction={'column'} spacing={1} alignItems={'stretch'} justifyContent={'flex-start'}>
                                <Stack direction={'column'} spacing={1.5} alignItems={'stretch'} justifyContent={'flex-start'}>
                                    <Grid container spacing={0}>
                                        <Grid item xs={12} style={{ fontWeight: 'bold', textAlign: 'left' }}>
                                            <Typography>
                                                <strong>Regulatory</strong>
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} style={{ fontWeight: 'bold', textAlign: 'left' }}>
                                            <Typography>{`${summaryData.Regulation.Region} Region Requirements`}</Typography>
                                        </Grid>

                                        <Grid item xs={12} style={{ fontWeight: 'bold', textAlign: 'left' }}>
                                            <Typography>{`${summaryData.Regulation.Country} Country Requirements`}</Typography>
                                        </Grid>

                                        <Grid item xs={12} style={{ fontWeight: 'bold', textAlign: 'left' }}>
                                            <Typography>{`${summaryData.Regulation.State} State Requirements`}</Typography>
                                        </Grid>

                                        <Grid item xs={12} style={{ fontWeight: 'bold', textAlign: 'left' }}>
                                            <Typography>{`${summaryData.Regulation.Port} Port Requirements`}</Typography>
                                        </Grid>

                                        <Grid item xs={12} style={{ fontWeight: 'bold', textAlign: 'left' }}>
                                            <Typography>{`${summaryData.Regulation.MOU} MOU Requirements`}</Typography>
                                        </Grid>

                                        <Grid item xs={12} style={{ fontWeight: 'bold', textAlign: 'left' }}>
                                            <Typography>
                                                <strong> Incentives</strong>
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} style={{ textAlign: 'left' }}>
                                            <Typography>{`${summaryData.Incentive.Port} Port Incentives`}</Typography>
                                        </Grid>

                                        <Grid item xs={12} style={{ fontWeight: 'bold', textAlign: 'left' }}>
                                            <Typography>
                                                <strong>Alt. Fuels</strong>
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} style={{ textAlign: 'left' }}>
                                            <Typography>{`${summaryData.Fuel.Country} Alt. Fuel Facilities`}</Typography>
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{ border: '1px solid #da1f33' }} orientation="horizontal" flexItem />
                                    <Button size="small" variant="contained" onClick={() => handleSeeMore()}>
                                        See More
                                    </Button>
                                    <Button size="small" variant="outlined" href="https://abs.jotform.com/222194343602954" target="_blank">
                                        Give Feedback
                                    </Button>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                </>
            )}
        </>
    );
};

const DestinationRestriction = (props) => {
    const { aisData, handleRegulationData } = props;
    const { instance, accounts } = useMsal();

    const [isLoading, setIsLoading] = useState(true);
    const [matchData, setMatchData] = useState();
    const [portCodeCountry, setPortCodeCountry] = useState();

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            await getData();
            await getPortCodeCountryLookupData();
            setIsLoading(false);
        })();
    }, [aisData]);

    const getData = async () => {
        if (aisData && aisData.Destination != '') {
            const authToken = await getAuthToken(instance, accounts);
            const mapLocation = await SearchService.mapLocation(authToken, aisData.Destination);

            setMatchData(mapLocation);
        }
    };

    const getPortCodeCountryLookupData = async () => {
        if (aisData && aisData.Destination != '') {
            const authToken = await getAuthToken(instance, accounts);
            const portCodeCountryResult = await PortCodeService.getSearchCountryLookup(authToken, aisData.Destination);
            setPortCodeCountry(portCodeCountryResult);
        }
    };

    const LocationLabel = () => {
        return (
            <>
                {matchData.MatchResultType === 'PortCode' && <ErrorOutlineOutlinedIcon sx={{ height: '18px' }} />}
                {matchData.MatchResultType === 'Port' && <DirectionsBoatIcon sx={{ height: '18px' }} />}
                {matchData.MatchResultType === 'State' && <AccountBalanceIcon sx={{ height: '18px' }} />}
                {matchData.MatchResultType === 'Country' && <FlagIcon sx={{ height: '18px' }} />}
                {matchData.MatchResultType === 'Region' && <SouthAmericaIcon sx={{ height: '18px' }} />}

                <Stack direction={'column'} spacing={0.5} alignItems={'flex-start'}>
                    {matchData.MatchResultType === 'Port' && `${matchData.Port}`}
                    {matchData.MatchResultType === 'State' && `${matchData.State}`}
                    {matchData.MatchResultType === 'Country' && `${matchData.Country}`}
                    {matchData.MatchResultType === 'Region' && `${matchData.Region}`}
                    <Typography variant="caption" display="block">
                        {matchData.MatchResultType === 'Port' && !matchData.State && `${matchData.Country}, ${matchData.Region}`}
                        {matchData.MatchResultType === 'Port' && matchData.State && `${matchData.State}, ${matchData.Country}, ${matchData.Region}`}
                        {matchData.MatchResultType === 'State' && `${matchData.Country}, ${matchData.Region}`}
                        {matchData.MatchResultType === 'Country' && `${matchData.Region}`}
                    </Typography>
                </Stack>
            </>
        );
    };

    return (
        <>
            {isLoading && <LoadingMessasge />}
            {!isLoading && (
                <>
                    {!aisData && (
                        <>
                            <Stack direction={'row'} spacing={1} alignItems={'center'} justifyContent={'flex-start'}>
                                <ErrorOutlineOutlinedIcon sx={{ height: '18px' }} />
                                <Stack direction={'column'} spacing={0.5} alignItems={'flex-start'}>
                                    Not Found
                                    <Typography variant="caption" display="block">
                                        Note: AIS Location Not Available.
                                    </Typography>
                                </Stack>
                            </Stack>
                        </>
                    )}

                    {!matchData && aisData && portCodeCountry && portCodeCountry.Country === '' && (
                        <Stack direction={'row'} spacing={1} alignItems={'center'} justifyContent={'flex-start'}>
                            <ErrorOutlineOutlinedIcon sx={{ height: '18px' }} />
                            <Stack direction={'column'} spacing={0.5} alignItems={'flex-start'}>
                                {aisData.Destination}
                                <Typography variant="caption" display="block">
                                    Note: Unable to match AIS Location.
                                </Typography>
                            </Stack>
                        </Stack>
                    )}

                    {!matchData && aisData && portCodeCountry && portCodeCountry.Country && (
                        <Stack direction={'row'} spacing={1} alignItems={'center'} justifyContent={'flex-start'}>
                            <ErrorOutlineOutlinedIcon sx={{ height: '18px' }} />
                            <Stack direction={'column'} spacing={0.5} alignItems={'flex-start'}>
                                {portCodeCountry.Country}
                                <Typography variant="caption" display="block">
                                    No Restrictions found for this Location.
                                </Typography>
                            </Stack>
                        </Stack>
                    )}

                    {matchData && matchData.MatchResultType === 'PortCode' && (
                        <Stack direction={'row'} spacing={1} alignItems={'center'} justifyContent={'flex-start'}>
                            <ErrorOutlineOutlinedIcon sx={{ height: '18px' }} />
                            <Stack direction={'column'} spacing={0.5} alignItems={'flex-start'}>
                                {matchData.Country}
                                <Typography variant="caption" display="block">
                                    No Restrictions found for this Location.
                                </Typography>
                            </Stack>
                        </Stack>
                    )}

                    {matchData && matchData.MatchResultType !== 'PortCode' && (
                        <Tooltip title={<DestinationPortToolTip matchData={matchData} handleRegulationData={handleRegulationData} />} placement="left" arrow>
                            <Stack direction={'row'} spacing={1} alignItems={'center'} justifyContent={'flex-start'}>
                                <LocationLabel />
                            </Stack>
                        </Tooltip>
                    )}
                </>
            )}
        </>
    );
};

const VesselList = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(true);
    const [vesselList, setVesselList] = useState([]);
    const [vesselListFiltered, setVesselListFiltered] = useState([]);

    const { instance, accounts } = useMsal();

    const [regulationData, setRegulationData] = useState();
    const [matchData, setMatchData] = useState();
    const [subjectList, setSubjectList] = useState();

    useEffect(() => {
        (async () => {
            await getVessel();
        })();
    }, []);

    const handleSetRegulationData = (regulationData, matchData) => {
        setSubjectList();
        setRegulationData(regulationData);
        setMatchData(matchData);

        refRegulationList.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
        });
    };

    const handleSetRegulationSubject = async () => {
        const authToken = await getAuthToken(instance, accounts);
        const subjectListData = await SubjectListService.getSubjectList(authToken, matchData.MatchResultTypeId, matchData.MatchResultType);

        if (!subjectListData) {
            console.error('Error : Getting Regulation Subject.');
            return;
        }

        setSubjectList(subjectListData);
    };

    useEffect(() => {
        const fetchRegulationSubject = async () => {
            if (regulationData) {
                await handleSetRegulationSubject();
            }
        };

        // Dismount subject list
        fetchRegulationSubject();
    }, [regulationData]);

    const populateVesselListInfo = async (tempVesselList) => {
        if (!tempVesselList || tempVesselList.length <= 0) {
            return tempVesselList;
        }

        const authToken = await getAuthToken(instance, accounts);

        // This is a non-blocking promise all implementation.
        // To ensure UI is rendered asynchronously.
        tempVesselList = Promise.all(
            tempVesselList.map(async (item) => {
                const aisData = await AisService.vesselLocation(authToken, item.ImoNumber);
                item.Timestamp_Format = aisData && aisData.Timestamp ? `${customUtils.dateFormat(aisData.Timestamp)} ${customUtils.timeConvert(aisData.Timestamp)}` : 'AIS Data Not Available';
                item.AisDestination_Format = <VesselDestination aisData={aisData} />;
                item.DestinationRestriction_Format = <DestinationRestriction aisData={aisData} handleRegulationData={handleSetRegulationData} />;

                return item;
            })
        );

        return tempVesselList;
    };

    const getVessel = async () => {
        setIsLoading(true);
        const authToken = await getAuthToken(instance, accounts);
        const result = await VesselService.getAll(authToken);

        if (!result) {
            return;
        }

        const vesselData = result.map((item) => {
            item.VesselName_Format = (
                <>
                    {item.VesselName}
                    <Typography variant="caption" display="block">
                        {item.VesselType}
                    </Typography>
                </>
            );
            item.ImoNumber_Format = <center>{item.ImoNumber}</center>;
            item.ClassNumber_Format = <center>{item.ClassNumber}</center>;
            item.AisPosition_Format = <LoadingMessage />;
            item.AisDestinationPort_Format = <LoadingMessage />;

            return item;
        });

        setVesselList(vesselData);
        setVesselListFiltered(vesselData.slice(0 * tablePage[0], tablePage[0] * (0 + 1)));

        setIsLoading(false);
    };

    const applyFilter = (filtersObj) => {
        setTableLoading(true);
        const { pageIndex, pageSize } = filtersObj;
        const tempData = vesselList.slice(pageIndex * pageSize, pageSize * (pageIndex + 1));

        populateVesselListInfo(tempData).then((data) => {
            setVesselListFiltered(data);
            setTableLoading(false);
        });
    };

    const handleChipDelete = () => {
        setRegulationData(null);
        setMatchData(null);
        setSubjectList(null);

        refVesselList.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    };

    const getChipLabel = () => {
        if (matchData.MatchResultType === 'Region') return `Region : ${matchData.Region}`;
        else if (matchData.MatchResultType === 'Country') return `Country : ${matchData.Country}`;
        else if (matchData.MatchResultType === 'State') return `State : ${matchData.State}`;
        else if (matchData.MatchResultType === 'Port') return `Port : ${matchData.Port}`;

        return 'Error';
    };

    return (
        <div className="wrapper-content">
            {isLoading && (
                <Box
                    sx={{
                        textAlign: 'center',
                        marginTop: '132px',
                        minHeight: '700px'
                    }}
                >
                    <CircularProgress />
                </Box>
            )}
            {!isLoading && (
                <>
                    <Stack direction={'column'} spacing={2} ref={refVesselList}>
                        <Alert severity="info">
                            <AlertTitle>AIS Disclaimer</AlertTitle>
                            The Associated Destination may not be the actual or intended destination of the vessel.
                        </Alert>
                        <AbsReactTable
                            loading={tableLoading}
                            tableType={TABLE.TYPE.BASIC}
                            columns={tableColumns}
                            data={vesselListFiltered}
                            dataCount={vesselList.length}
                            pagination={true}
                            small={true}
                            allowColumnsShowHide={false}
                            applyFilterCallback={applyFilter}
                            pageOptions={tablePage}
                        />
                    </Stack>
                    <Stack direction={'column'} spacing={2} ref={refRegulationList}>
                        {regulationData && subjectList && (
                            <RegulationList regulationData={regulationData} chipLabel={getChipLabel()} onChipDelete={handleChipDelete} regulationSubject={null} subjectListSelect={subjectList} />
                        )}
                    </Stack>
                </>
            )}
        </div>
    );
};

export default VesselList;
